import React, { createContext, useContext, useRef, useCallback } from 'react';
import isArray from 'lodash.isarray';
import isEqual from 'lodash.isequal';
import memoizeOne from 'memoize-one';

/* eslint-disable no-undef,eqeqeq,no-restricted-globals,@typescript-eslint/no-explicit-any */
const root = ((typeof self == 'object' && self.Object == Object && self) ||
    (typeof global == 'object' && global.Object == Object && global));
const { window } = root;

function digitalDataProviderInitializer(defaultDigitalData) {
    function isFunction(val) {
        return typeof val === 'function';
    }
    const digitalData = Object.assign({}, defaultDigitalData);
    const DigitalDataContext = createContext(defaultDigitalData);
    const DigitalDataUpdaterContext = createContext({
        digitalDataUpdater: () => (data) => data,
    });
    const setDigitalData = (key, dataOrSetter) => {
        const data = isFunction(dataOrSetter) ? dataOrSetter(digitalData[key]) : dataOrSetter;
        if (!digitalData) {
            return;
        }
        if (isArray(data)) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            digitalData[key] = [...data];
        }
        else {
            digitalData[key] = Object.assign(Object.assign({}, digitalData[key]), data);
        }
    };
    const DigitalDataProvider = ({ children, __digitalData }) => {
        if (window) {
            window.digitalData = digitalData;
        }
        return (React.createElement(DigitalDataContext.Provider, { value: (__digitalData || digitalData) },
            React.createElement(DigitalDataUpdaterContext.Provider, { value: { digitalDataUpdater: setDigitalData } }, children)));
    };
    return {
        DigitalDataContext,
        DigitalDataUpdaterContext,
        DigitalDataProvider,
    };
}

function __trackSatellite(event, data, { onDebug, onError }) {
    onDebug(event, data);
    if (window && window._satellite && event) {
        try {
            window._satellite.track(event, data);
        }
        catch (err) {
            onError(err);
        }
    }
}

const noop = () => {
    /* noop */
};

function analyticsContextInitializer(digitalDataContext, digitalDataUpdaterContext, options) {
    function isFunction(val) {
        return typeof val === 'function';
    }
    const defaultAnalyticsContextValue = () => ({
        trackSatellite: (event, data) => __trackSatellite(event, data, options),
        setDigitalData: noop,
    });
    const AnalyticsContext = createContext(defaultAnalyticsContextValue);
    const useAnalyticsService = (trackingKey) => {
        const analyticsService = useContext(AnalyticsContext);
        return analyticsService(trackingKey);
    };
    const setterMap = {};
    const AnalyticsContextProvider = ({ children }) => {
        const digitalData = useContext(digitalDataContext);
        const { digitalDataUpdater } = useContext(digitalDataUpdaterContext);
        const trackingList = useRef({});
        // Loading count tracks the number of items that are currently loading in the app. Once it reaches zero
        // the AnalyticsService will unblock the callqueue and begin firing trackSatellite calls.
        const loadingCount = useRef(0);
        // callQueue holds a list of trackSatellite events that will fire once all the blocking data on the page
        // has fully loaded.
        const callQueue = useRef([]);
        const runQueue = useCallback(() => {
            const hasItems = Object.keys(trackingList.current).length > 0;
            const hasQueue = callQueue.current.length > 0;
            if (hasItems && loadingCount.current === 0 && hasQueue) {
                callQueue.current.forEach(({ event, data }) => {
                    __trackSatellite(event, data, options);
                });
                callQueue.current = [];
            }
        }, []);
        const updateCallQueue = useCallback(newQueue => {
            callQueue.current = newQueue;
            runQueue();
        }, [runQueue]);
        const updateLoadingCount = useCallback(newValue => {
            loadingCount.current = newValue;
            runQueue();
        }, [runQueue]);
        // setLoading is invoked every time the useAnalytics hook code is run. It checks to see if you
        // want to track the data and if so it adds the trackingKey to our trackingList and begins
        // tracking the loading state.
        const setLoading = useCallback((trackingKey) => {
            const list = trackingList.current;
            if (trackingKey && !list[trackingKey]) {
                trackingList.current = Object.assign(Object.assign({}, list), { [trackingKey]: { loaded: false } });
                updateLoadingCount(loadingCount.current + 1);
            }
        }, [updateLoadingCount]);
        // managedTrackSatellite is used in conjunction with managedSetDigitalData.
        // It is exported as trackSatellite. Instead of directly dispatching the satellite event,
        // it will wait until all of the tracked loading states have been resolved
        const managedTrackSatellite = useCallback((event, data) => {
            if (event) {
                updateCallQueue([...callQueue.current, { event, data }]);
            }
        }, [updateCallQueue]);
        // managedSetDigitalData is exported as setDigitalData. It always updates the digitalData but
        // if the data is being tracked then it also removes the loading state.
        const managedSetDigitalData = useCallback(trackingKey => (key, value) => {
            const list = trackingList.current;
            const val = isFunction(value) ? value(digitalData[key]) : value;
            if (!isEqual(val, digitalData[key])) {
                digitalDataUpdater(key, val);
            }
            if (trackingKey && list[trackingKey] && !list[trackingKey].loaded) {
                updateLoadingCount(loadingCount.current - 1);
                list[trackingKey].loaded = true;
            }
        }, [digitalDataUpdater, updateLoadingCount, digitalData]);
        const contextValue = useCallback((trackingKey) => {
            setLoading(trackingKey);
            // Since our managedDigitalData is a curried function we use a map
            // to ensure that it is always returning the same functional reference
            // and not breaking any of the shallow equality checks in hooks.
            const key = trackingKey || 'default';
            let digitalDataSetter = setterMap[key];
            if (!digitalDataSetter) {
                digitalDataSetter = memoizeOne(managedSetDigitalData(trackingKey));
                setterMap[key] = digitalDataSetter;
            }
            return {
                trackSatellite: managedTrackSatellite,
                setDigitalData: digitalDataSetter,
            };
        }, [setLoading, managedSetDigitalData, managedTrackSatellite]);
        return React.createElement(AnalyticsContext.Provider, { value: contextValue }, children);
    };
    return {
        AnalyticsContextProvider,
        useAnalyticsService,
    };
}

const defaultOptions = {
    onDebug: noop,
    onError: noop,
};
function analyticsServiceInitializer(defaultDigitalData, options) {
    const { DigitalDataContext, DigitalDataUpdaterContext, DigitalDataProvider, } = digitalDataProviderInitializer(defaultDigitalData);
    const computedOptions = Object.assign(Object.assign({}, defaultOptions), (options || {}));
    const { AnalyticsContextProvider, useAnalyticsService } = analyticsContextInitializer(DigitalDataContext, DigitalDataUpdaterContext, computedOptions);
    const AnalyticsServiceProvider = ({ children }) => {
        return (React.createElement(DigitalDataProvider, null,
            React.createElement(AnalyticsContextProvider, null, children)));
    };
    return {
        AnalyticsServiceProvider,
        useAnalyticsService,
    };
}

export { analyticsServiceInitializer };
